/**
 * Linear Assets — Railway Track Obstruction Alerts (Step 4b)
 *
 * Capabilities:
 *   • Near-real-time (&le; 6h) alerts on track gauge & ballast foreign objects
 *   • Immediate emergency speed restriction & maintenance dispatch
 */

import React, { useState, useEffect } from 'react';
import { Train, ShieldAlert, AlertTriangle, Clock, RefreshCw, CheckCircle2, Shield } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { linearAssetsApi } from '../../services/api';

export const LinearTrackObstructionsPage: React.FC = () => {
  const [obstructions, setObstructions] = useState<any[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);

  useEffect(() => {
    linearAssetsApi.getTrackObstructions()
      .then((res) => setObstructions(res.data?.obstructions || []))
      .catch((err) => setError(err?.message || 'Failed to load track obstructions'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Train className="w-7 h-7 text-red-600" /> Railway Track Obstruction Alerts
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Rapid optical &amp; thermal satellite detection (&le; 6h latency) for foreign objects, rockfall debris, and thermal rail buckling
          </p>
        </div>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      <div className="space-y-4">
        {obstructions.map((obs) => {
          const isActive = obs.status === 'active';
          return (
            <Card key={obs.id} className={`p-5 border-l-4 ${isActive ? 'border-red-600 bg-red-50/20 dark:bg-red-950/10' : 'border-gray-400'}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-base text-gray-900 dark:text-white">{obs.corridor_name} &bull; Track Km {obs.track_km}</h3>
                    <span className={`px-2 py-0.5 rounded font-bold uppercase text-[10px] ${isActive ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                      {obs.anomaly_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Detected: {new Date(obs.detected_at).toLocaleString()} &bull; Satellite Pass Latency: <strong>{obs.sla_latency_hours}h</strong> (Confidence: {obs.confidence_score}%)
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded text-xs font-bold uppercase ${isActive ? 'bg-red-600 text-white' : 'bg-green-100 text-green-700'}`}>
                  {obs.status}
                </span>
              </div>

              <p className="text-xs text-gray-800 dark:text-gray-200 mt-2 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                {obs.description}
              </p>

              <div className="mt-3 flex items-center justify-between text-xs text-red-700 dark:text-red-300 font-semibold pt-2 border-t border-gray-100 dark:border-gray-800">
                <span className="flex items-center gap-1">
                  <ShieldAlert className="w-4 h-4" /> Recommended Action: {obs.action_recommended}
                </span>
                {isActive && (
                  <Button variant="primary" size="sm">
                    Dispatch Emergency Track Gang
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default LinearTrackObstructionsPage;
